import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type AgencyForApproval, type Me } from "../lib/api";

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const pad = (n: number) => String(n).padStart(2, "0");

/** Platform admins approve or reject new agencies. Everyone else is sent back to their desk. */
export function Approvals({ me }: { me: Me }) {
  const [agencies, setAgencies] = useState<AgencyForApproval[] | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(() => load.run(async () => setAgencies((await api.agencies()).agencies)), [load.run]);
  useEffect(() => {
    if (me.user.platformAdmin) void reload();
  }, [me.user.platformAdmin, reload]);

  if (!me.user.platformAdmin) return <Navigate to="/today" replace />;

  const decide = (a: AgencyForApproval, action: "approve" | "reject") =>
    void act.run(async () => {
      await api.decideAgency(a.tenantId, action);
      await reload();
    });

  const setPlan = (a: AgencyForApproval, plan: "paid" | "free", note?: string) =>
    void act.run(async () => {
      await api.setAgencyPlan(a.tenantId, plan, note);
      await reload();
    });

  const pending = agencies?.filter((a) => a.approval === "pending") ?? [];
  const decided = agencies?.filter((a) => a.approval !== "pending") ?? [];

  return (
    <>
      <div className="page-head">
        <p className="label">Platform</p>
        <h1 className="display">
          New agencies, <em>vetted</em>.
        </h1>
        <p className="lede">
          Agencies that sign up wait here until you let them in. Invited team members don't need approval. Approved agencies pay by subscription unless you
          put them on the free plan.
        </p>
      </div>

      <ErrorNotice error={load.error ?? act.error} />

      <section className="section" aria-labelledby="pending-h">
        <div className="section-head">
          <h2 id="pending-h" className="label">
            Waiting
          </h2>
          <span className="label figures">{agencies ? `${pad(pending.length)} pending` : "…"}</span>
        </div>
        <ol className="index">
          {pending.map((a, i) => (
            <li key={a.tenantId}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <span className="primary">{a.name}</span>
                <span className="secondary">
                  <span className="status pending">{a.ownerEmail ?? "no owner"}</span>{" "}
                  <span className="figures">· signed up {dateFmt.format(new Date(a.createdAt))}</span>
                </span>
              </span>
              <span className="actions">
                <button type="button" className="link-btn" disabled={act.pending} onClick={() => decide(a, "approve")}>
                  Approve
                </button>
                <button type="button" className="link-btn danger" disabled={act.pending} onClick={() => decide(a, "reject")}>
                  Reject
                </button>
              </span>
            </li>
          ))}
          {agencies && pending.length === 0 && (
            <li>
              <span className="empty">Nobody waiting.</span>
            </li>
          )}
          {!agencies && !load.error && (
            <li>
              <span className="empty">Loading…</span>
            </li>
          )}
        </ol>
      </section>

      {decided.length > 0 && (
        <section className="section" aria-labelledby="decided-h">
          <div className="section-head">
            <h2 id="decided-h" className="label">
              Decided
            </h2>
            <span className="label figures">{pad(decided.length)}</span>
          </div>
          <ol className="index">
            {decided.map((a, i) => {
              const approved = a.approval === "approved";
              return (
                <li key={a.tenantId} className={approved ? undefined : "inactive"}>
                  <span className="num" aria-hidden>
                    {pad(i + 1)}
                  </span>
                  <span className="main">
                    <span className="primary">{a.name}</span>
                    <span className="secondary">
                      <span className={`status ${approved ? "" : "off"}`}>{a.approval}</span>{" "}
                      <span className="figures">
                        · {a.ownerEmail ?? "no owner"}
                        {a.decidedAt && ` · ${dateFmt.format(new Date(a.decidedAt))}`}
                        {` · ${a.seats} ${a.seats === 1 ? "person" : "people"}`}
                      </span>
                    </span>
                    {approved && <span className="secondary">{planLine(a)}</span>}
                  </span>
                  <span className="actions">
                    {approved && <PlanControl agency={a} pending={act.pending} onSet={(plan, note) => setPlan(a, plan, note)} />}
                    <button
                      type="button"
                      className={`link-btn ${approved ? "danger" : ""}`}
                      disabled={act.pending}
                      onClick={() => {
                        if (!approved || window.confirm(`Revoke ${a.name}? Everyone in it loses access until you approve it again.`)) {
                          decide(a, approved ? "reject" : "approve");
                        }
                      }}
                    >
                      {approved ? "Revoke" : "Approve"}
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </>
  );
}

const STATUS_WORDS: Record<string, string> = {
  none: "not subscribed yet",
  incomplete: "first payment going through",
  active: "subscribed",
  past_due: "payment failed, retrying",
  unpaid: "unpaid",
  canceled: "subscription ended",
  paused: "paused",
};

function planLine(a: AgencyForApproval): string {
  if (a.billingPlan === "free") return `Free plan${a.billingPlanNote ? `: ${a.billingPlanNote}` : ""}`;
  return `Paid plan, ${STATUS_WORDS[a.subscriptionStatus] ?? a.subscriptionStatus}`;
}

/** Puts an agency on the free plan for good (with a note saying why), or back on paid. */
function PlanControl({ agency, pending, onSet }: { agency: AgencyForApproval; pending: boolean; onSet: (plan: "paid" | "free", note?: string) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  if (agency.billingPlan === "free") {
    return (
      <button type="button" className="link-btn" disabled={pending} onClick={() => onSet("paid")}>
        Make paid
      </button>
    );
  }
  if (!open) {
    return (
      <button type="button" className="link-btn" disabled={pending} onClick={() => setOpen(true)}>
        Make free
      </button>
    );
  }
  return (
    <form
      className="inline-form compact"
      onSubmit={(e) => {
        e.preventDefault();
        onSet("free", note.trim() || undefined);
        setOpen(false);
      }}
    >
      <label className="field compact">
        <span className="label">Why (for your records)</span>
        <input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Pilot client" autoFocus />
      </label>
      <button className="link-btn" disabled={pending}>
        Make free
      </button>
      <button type="button" className="link-btn" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </form>
  );
}
